import { boxes, cb, tf } from './helpers.js';
/**
 * The widgets of Chubb's fillable PDF for revenue below RM1 billion (task 92, decision 0069), mapped
 * against the blank form and checked by filling every widget with its field's name. Fields only the broker
 * or director fill in, the declarations and the regulations table are left blank.
 */
export const CHUBB_ERM_STANDARD_PDF = {
    sha256: 'cf9f435bdbe898fcf9f25423040b5e54cede23df37f309aad376fd6d589bb81f',
    pages: 19,
    fields: {
        company_name: tf(3),
        website: tf(5),
        headquarters: tf(8),
        year_established: tf(7),
        employees: tf(9),
        security_contact: { table: { 0: { name: tf(11), role: tf(13), email: tf(16), phone: tf(17) } } },
        turnover_current: tf(135),
        turnover_next: tf(136),
        turnover_us_pct: tf(35),
        turnover_online_pct: tf(137),
        activities: tf(138),
        subsidiary: boxes({ yes: 11, no: 4 }),
        subsidiary_detail: tf(116),
        sanctions: boxes({ yes: 13, no: 12 }),
        overseas: boxes({ yes: 15, no: 14 }),
        overseas_list: tf(141),
        pii_records: tf(76),
        card_records: tf(82),
        health_records: tf(83),
        pci: boxes({ yes: 19, no: 18 }),
        pci_transactions: tf(81),
        pci_outsourced: boxes({ yes: 21, no: 20 }),
        pci_level: boxes({ level_1: 23, level_2: 24, level_3: 25, level_4: 26, not_compliant: 27 }),
        privacy_policy: boxes({ yes: 29, no: 28 }),
        security_policy: boxes({ yes: 31, no: 30 }),
        classification_policy: boxes({ yes: 33, no: 32 }),
        security_staff: boxes({ yes: 35, no: 34 }),
        ir_plan: boxes({ yes: 37, no: 36 }),
        privacy_monitoring: boxes({ yes: 39, no: 38 }),
        group_baseline: boxes({ yes: 42, no: 40 }),
        regulations_identified: boxes({ yes: 415, no: 414, partial: 416 }),
        compliance_assessed: boxes({ yes: 419, no: 417, partial: 418 }),
        biometric: boxes({ yes: 424, no: 423 }),
        pii_largest_store: tf(145),
        pii_need_to_know: boxes({ yes: 428, no: 427 }),
        pii_controls: boxes({
            microsegmentation: 429,
            anonymisation: 435,
            pseudonymisation: 437,
            tokenisation: 430,
            db_encryption: 436,
            transit_encryption: 438,
            dlp: 439,
            other: 447,
        }),
        pii_outsourced: boxes({ yes: 450, no: 448, partial: 449 }),
        processor_contracts: boxes({ yes: 453, no: 451, partial: 452 }),
        processor_breach_terms: boxes({ yes: 456, no: 454, partial: 455 }),
        processor_recourse_waived: boxes({ yes: 459, no: 457, partial: 458 }),
        hosted_centrally: boxes({ yes: 143, no: 142, partial: 138 }),
        segmentation: boxes({
            vlan: 144,
            host_firewalls: 148,
            acl: 149,
            air_gap: 150,
            sdn: 151,
            least_privilege: 152,
            other: 153,
        }),
        testing: boxes({ external_pentest: 460, internal_pentest: 461, waf: 462 }),
        mobile_access: boxes({ yes: 411, no: 410 }),
        mdm_company_pct: tf(1012),
        mdm_byod_pct: tf(1014),
        remote_access: boxes({ yes: 470, no: 469 }),
        remote_methods: boxes({ vpn: 471, mfa: 474, sso_mfa: 472, ztna: 475, encryption: 473, other: 476 }),
        remote_scope: boxes({ yes: 479, no: 478, partial: 477 }),
        rdp_use: boxes({ not_used: 195, internal_only: 198, remote_access: 196, other: 199 }),
        rdp_protections: boxes({ vpn: 201, mfa: 204, nla: 202, honeypots: 205, other: 203 }),
        iam_programme: boxes({ yes: 208, no: 207 }),
        service_accounts: tf(1026),
        admin_users: tf(10111),
        wide_admins: tf(10112),
        domain_admins: tf(10117),
        admin_justification: tf(109),
        account_controls: boxes({
            accounts_audited: 2044,
            logs_90_days: 2048,
            separate_admin_accounts: 20127,
            paw: 2049,
            directory_monitored: 20128,
            pam: 20129,
            privileged_mfa: 20130,
        }),
        mfa_independent: boxes({ yes: 482, no: 481, na: 480 }),
        email_security: boxes({
            mfa: 237,
            external_tag: 483,
            spf: 238,
            dkim: 487,
            gateway: 243,
            malware_scan: 491,
            quarantine: 244,
            secure_send: 485,
            sandboxing: 245,
            macros: 489,
            phishing_training: 246,
            other: 493,
        }),
        bcp: boxes({ yes: 259, no: 258 }),
        drp: boxes({ yes: 496, no: 495 }),
        backup_protections: boxes({
            immutable: 2013,
            offline: 2015,
            separate_account: 2014,
            mfa: 2016,
            encrypted: 2018,
            cloud_segmented: 2017,
            other: 2019,
        }),
        backup_testing: boxes({
            full_restore: 497,
            recoverability: 498,
            integrity: 501,
            ransomware_plan: 502,
            malware_scanned: 505,
            email_backups: 506,
        }),
        critical_systems: {
            table: {
                0: { system: tf(1040), provider: tf(1044), bia: tf(1041) },
                1: { system: tf(1042), provider: tf(1045), bia: tf(1043) },
                2: { system: tf(1052), provider: tf(1054), bia: tf(1053) },
                3: { system: tf(1058), provider: tf(1062), bia: tf(1060) },
                4: { system: tf(1059), provider: tf(1063), bia: tf(1061) },
            },
        },
        alternative_systems: boxes({ yes: 511, no: 510, partial: 509 }),
        alternate_power: boxes({ yes: 291, no: 289 }),
        extra_bandwidth: boxes({ yes: 290, no: 288 }),
        generators_tested: boxes({ yes: 299, no: 298 }),
        secure_development: boxes({ yes: 513, no: 512, na: 5010 }),
        ddos: boxes({ yes: 3028, no: 3026 }),
        monitoring: boxes({
            ids: 2025,
            ips: 2026,
            antimalware: 20146,
            web_filtering: 2027,
            isolation: 20140,
            advanced_endpoint: 2032,
            edr: 2033,
            mdr: 2036,
            xdr: 2038,
            manual_log_review: 20144,
            soc: 20145,
            managed_firewall: 20143,
            threat_intel: 20141,
            other: 2054,
            siem: 20147,
            soar: 20142,
            protective_dns: 20180,
        }),
        siem_pct: tf(10120),
        edr_pct: tf(10121),
        edr_auto: boxes({ yes: 515, no: 514, partial: 5011 }),
        edr_provider: tf(10122),
        edr_to_siem: boxes({ yes: 519, no: 518, partial: 5012, na: 520 }),
        inventory: boxes({ yes: 3013, no: 3011 }),
        inventory_pct: tf(1070),
        scan_scope_pct: tf(10116),
        scan_internal: tf(10142),
        scan_external: tf(10141),
        asset_risk_levels: boxes({ yes: 3017, no: 3012 }),
        eol: boxes({ yes: 3031, no: 3018 }),
        eol_critical: boxes({ yes: 3032, no: 3027 }),
        eol_sensitive: boxes({ yes: 3033, no: 3029 }),
        eol_no_internet: boxes({ yes: 3034, no: 3030 }),
        eol_segregated: boxes({ yes: 3038, no: 3036 }),
        eol_detail: tf(10130),
        ports_closed: boxes({ yes: 524, no: 521 }),
        patch_process: boxes({ yes: 525, no: 522 }),
        patch_days_low: tf(10123),
        patch_days_medium: tf(10124),
        patch_days_high: tf(10125),
        patch_days_critical: tf(10126),
        vendor_risk: boxes({ yes: 536, no: 533 }),
        vendor_checks: boxes({
            infosec_cert: 2051,
            resilience_cert: 20137,
            pentest: 20132,
            backup_review: 20138,
            rating_service: 20135,
            sla: 20139,
            mfa_review: 2052,
            dpia: 20151,
            dpa: 20148,
            other: 20150,
        }),
        recourse_waived: boxes({ never: 2053, sometimes: 2056, mostly: 20149 }),
        uses_cloud: boxes({ yes: 540, no: 538 }),
        cloud_policy: boxes({ yes: 541, no: 539, na: 537 }),
        cloud_controls: boxes({
            casb: 20131,
            sase: 20133,
            ztna: 20152,
            sso: 20154,
            mfa_critical: 20156,
            mfa_other: 20155,
            other: 20157,
        }),
        trademarks_screened: boxes({ yes: 341, no: 340 }),
        releases_obtained: boxes({ yes: 343, no: 342 }),
        legal_review: boxes({ yes: 345, no: 344 }),
        outsourced_content: boxes({ yes: 347, no: 346 }),
        indemnities: boxes({ yes: 349, no: 348 }),
        policies_reviewed: boxes({ yes: 351, no: 350 }),
        losses: boxes({
            data_breach: 20134,
            cyber_incident: 20136,
            system_failure: 20159,
            media_claim: 20153,
            regulatory_action: 20160,
            supplier_breach: 201020,
            supplier_incident: 20161,
        }),
        loss_detail: { columns: { what: tf(10114), impact: tf(10136), mitigation: tf(10137) } },
        circumstances: boxes({ yes: 370, no: 369 }),
        circumstances_detail: tf(1096),
        bio_employees: boxes({ yes: 549, no: 542 }),
        bio_contractors: boxes({ yes: 552, no: 545 }),
        bio_customers: boxes({ yes: 551, no: 544 }),
        bio_release: boxes({ yes: 550, no: 543 }),
        bio_policy: boxes({ yes: 554, no: 547 }),
        bio_explicit_consent: boxes({ yes: 555, no: 548 }),
        bio_records: tf(101023),
        ot_policy: boxes({ yes: 557, no: 556 }),
        ot_sites: tf(101028),
        ot_remote: boxes({ yes: 561, no: 560 }),
        ot_monitored: boxes({ yes: 564, no: 562 }),
        ot_backups: boxes({ yes: 567, no: 566 }),
        acquisitions: tf(1089),
        acquisition_detail: tf(10100),
        acquisition_review: boxes({ before: 2080, before_integration: 2089, rarely: 2090, other: 2091 }),
        pi_insurance: boxes({ yes: 4025, no: 4020 }),
        pi_cyber_exclusion: boxes({ yes: 4028, no: 4023 }),
        pos_segregated: boxes({ yes: 4011, no: 409 }),
        emv_pct: tf(101017),
        payment_providers: tf(10106),
        pos_monitored: boxes({ yes: 4012, no: 4010 }),
        franchise: boxes({ yes: 4014, no: 4013 }),
        franchise_detail: tf(10108),
        current_cover: {
            table: {
                cyber: {
                    _tick: cb(4015),
                    limit: tf(148),
                    excess: tf(156),
                    premium: tf(157),
                    insurer: tf(155),
                    expiry: tf(158),
                },
                crime: {
                    _tick: cb(4016),
                    limit: tf(159),
                    excess: tf(160),
                    premium: tf(161),
                    insurer: tf(162),
                    expiry: tf(163),
                },
                professional_indemnity: {
                    _tick: cb(4017),
                    limit: tf(164),
                    excess: tf(165),
                    premium: tf(166),
                    insurer: tf(167),
                    expiry: tf(168),
                },
            },
        },
        limit_cyber_expenses: boxes({ 1000000: 4030, 2000000: 4031, 3000000: 4032, 4000000: 4034, 5000000: 4035 }, { tick: 4018, other: 193 }),
        limit_cyber_liability: boxes({ 1000000: 4033, 2000000: 4038, 3000000: 4036, 4000000: 4037, 5000000: 4039 }, { tick: 4029, other: 199 }),
        phishing_exercises: boxes({ yes: 4061, no: 4050 }),
        password_manager: boxes({ yes: 4062, no: 4058 }),
        training_modules: boxes({ yes: 4063, no: 4059 }),
        ir_preparedness: boxes({ yes: 4064, no: 4060 }),
    },
};
//# sourceMappingURL=chubb-erm-standard-pdf.js.map